import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import ProfileVisibilitySwitch from "./ProfileVisibilitySwitch";
import ConfirmStopModal from "../ConfirmStopModal";
import NoticeBanner from "../NoticeBanner";
import { useIsDesktop } from "../hooks/useIsDesktop";

/**
 * マイページ公開設定セクション
 *
 * - state管理
 * - API通信
 * - モーダル表示
 *
 * を担当する
 */
const ProfileVisibilitySection = ({
  sectionStyle,
  sectionTitleStyle,
  sectionContentStyle,
  handleSessionExpired,
  profilePublicFlag,
  isSkillSheetBasicCompleted,
}) => {
  const isDesktop = useIsDesktop();

  // 公開状態
  const [isProfilePublic, setIsProfilePublic] = useState(false);

  const PROFILE_PUBLIC_MESSAGE = `公開すると、共有リンクを知っているユーザーがあなたのマイページを閲覧できるようになります。

公開される情報：
・イニシャル
・年齢 / 性別
・エンジニア歴 / 国籍 / 最終学歴
・自己PR / 実績
・プロジェクト概要
・スキル`;

  const PROFILE_PRIVATE_MESSAGE =
    "非公開にすると、共有リンクからマイページを閲覧できなくなります。";

  const modalConfig = isProfilePublic
    ? {
        title: "マイページを非公開にします",
        message: PROFILE_PRIVATE_MESSAGE,
        subMessage: "マイページを非公開にしてもよろしいですか？",
        confirmLabel: "非公開にする",
      }
    : {
        title: "マイページを公開します",
        message: PROFILE_PUBLIC_MESSAGE,
        subMessage: "マイページを公開してもよろしいですか？",
        confirmLabel: "公開する",
      };

  useEffect(() => {
    setIsProfilePublic(Boolean(profilePublicFlag));
  }, [profilePublicFlag]);

  // 更新中状態
  const [isProfileUpdating, setIsProfileUpdating] = useState(false);

  // エラー状態
  const [profileStatusError, setProfileStatusError] = useState(false);

  // 確認モーダル開閉
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // エラーメッセージ
  const profileStatusErrorMessage = "公開設定の変更に失敗しました。";

  // Switch変更時
  // 即変更せずモーダルを開く
  const handleChangeProfileStatus = () => {
    setIsProfileModalOpen(true);
  };

  // キャンセル時
  const handleCancelProfileVisibility = () => {
    setIsProfileModalOpen(false);
  };

  // 公開/非公開の確定処理
  const handleConfirmProfileVisibility = async () => {
    if (isProfileUpdating) return;

    const nextProfilePublic = !isProfilePublic;

    setIsProfileUpdating(true);
    setProfileStatusError(false);

    try {
      const response = await fetch("/profile_visibility_setting", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
        body: JSON.stringify({
          profile_public_flag: nextProfilePublic,
        }),
      });

      // セッション切れ対応
      if (handleSessionExpired(response)) {
        return;
      }

      // 通信失敗
      if (!response.ok) {
        throw new Error();
      }

      // レスポンスを取得
      const data = await response.json();

      // サーバーの値を state に反映
      setIsProfilePublic(data.profile_public_flag);

      // toast表示
      toast.success(
        data.profile_public_flag
          ? "マイページを公開しました。"
          : "マイページを非公開にしました。",
      );
    } catch (error) {
      setProfileStatusError(true);

      toast.error(profileStatusErrorMessage);
    } finally {
      setIsProfileUpdating(false);
      setIsProfileModalOpen(false);
    }
  };

  return (
    <>
      {/* マイページ公開設定 */}
      <Box sx={sectionStyle}>
        <Typography sx={sectionTitleStyle}>マイページ公開設定</Typography>

        {isSkillSheetBasicCompleted ? (
          <Box sx={sectionContentStyle}>
            <ProfileVisibilitySwitch
              isProfilePublic={isProfilePublic}
              isProfileUpdating={isProfileUpdating}
              profileStatusError={profileStatusError}
              profileStatusErrorMessage={profileStatusErrorMessage}
              onToggleProfile={handleChangeProfileStatus}
            />
          </Box>
        ) : (
          <Box
            sx={{
              ...sectionContentStyle,
              width: isDesktop ? 340 : "100%",
            }}
          >
            <NoticeBanner
              text={
                "基本情報を登録すると、\n公開設定を変更できるようになります。"
              }
            />
          </Box>
        )}
      </Box>

      {/* 確認モーダル */}
      <ConfirmStopModal
        open={isProfileModalOpen}
        onClose={handleCancelProfileVisibility}
        onConfirm={handleConfirmProfileVisibility}
        title={modalConfig.title}
        message={modalConfig.message}
        subMessage={modalConfig.subMessage}
        confirmLabel={modalConfig.confirmLabel}
        isSubmitting={isProfileUpdating}
      />
    </>
  );
};

export default ProfileVisibilitySection;
